/* @flow */

import React, { Component } from 'react';
import { noop } from 'lodash/fp';
import classNames from 'classnames';
import useSheet from 'app/jss';
import IconButton from 'app/components/commons/IconButton';
import FriendTextInput from 'app/components/friends/common/FriendTextInput';
import Tappable from 'app/components/commons/Tappable';
import t from 'app/utils/translate';

type InvalidConfig = {
  id: string,
  isInvalid: (title: string) => boolean,
  reason: string,
};

type Props = {
  classes: Object,
  title?: string,
  onTitleSave?: (title: string) => void,
  onTitleChange?: (title: string) => void,
  onShrink?: () => void,
  onRemove?: () => void,
  leftIcon?: string,
  rightIcon?: string,
  invert?: boolean,
  invalidConfig?: Array<InvalidConfig>,
  editMode?: boolean,
};

type State = {
  editMode: boolean,
  invalid: InvalidConfig,
};

class GroupEditableTitle extends Component<Props, State> {
  props: Props;

  constructor(props: Props) {
    super(props);
    this.state = {
      editMode: !props.title || props.editMode,
      invalid: undefined,
    };
  }

  static defaultProps = {
    title: '',
    onTitleSave: noop,
    onTitleChange: noop,
    onRemove: undefined,
    onShrink: undefined,
    invert: false,
    leftIcon: 'icon-bin',
    rightIcon: 'icon-cross',
    invalidConfig: [],
    editMode: false,
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (this.props.editMode !== nextProps.editMode) {
      this.setState({ editMode: nextProps.editMode });
    }
  }

  onTitleSave = (title: string) => {
    const isInvalid = !!(this.state.invalid);
    this.props.onTitleSave(title, isInvalid);

    if (!isInvalid) {
      this.endEditing();
    }
  }

  onTitleChange = (title: string) => {
    const {
      invalidConfig,
      onTitleChange,
    } = this.props;
    const { invalid } = this.state;
    const config = invalidConfig.find(val => val.isInvalid(title));
    // if no longer invalid, update
    // if invalid and not the same invalid id as before - update
    if ((!config && invalid) || (!invalid || invalid.id !== config.id)) {
      this.setState({ invalid: config });
    }
    onTitleChange(title, !!(config));
  }

  removeGroup = () => {
    this.props.onRemove();
  }

  shrinkGroup = () => {
    this.props.onShrink();
  }

  startEditing = () => {
    this.setState({ editMode: true });
  }

  endEditing = () => {
    this.setState({ editMode: false });
  }

  setTextRef = (node) => {
    this.textRef = node;
  }

  render() {
    const {
      classes,
      title,
      invert,
      onShrink,
      onRemove,
      leftIcon,
      rightIcon,
    } = this.props;
    const {
      editMode,
      invalid,
    } = this.state;

    const isEditMode = !!(editMode || !title || invalid);

    return (
      <div className={classNames(classes.groupTitle, { invert })}>
        {onRemove &&
          <IconButton
            type={leftIcon}
            onClick={this.removeGroup}
            className={classNames(classes.icon, 'left')}
          />}
        <Tappable
          component="div"
          onTap={this.startEditing}
        >
          <FriendTextInput
            inputClasses={classNames(classes.input, { [classes.center]: !isEditMode })}
            editMode={isEditMode}
            text={title}
            onTextChange={this.onTitleChange}
            onBlur={this.onTitleSave}
            onSubmit={this.onTitleSave}
            uppercase
            condensed
            disabled={!editMode}
            placeholderIcon="icon-edit"
            placeholderOnly
            placeholder={t('group name')}
            invalid={!!invalid}
            invalidReason={invalid && invalid.reason}
          />
        </Tappable>
        {onShrink && (
          <IconButton
            type={rightIcon}
            onClick={this.shrinkGroup}
            className={classNames(classes.icon, 'right')}
          />
        )}
      </div>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    invalid: {
      color: theme.color.danger,
    },
    groupTitle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',

      '&.invert input': {
        color: theme.color.basis6,
      },
    },
    input: {
      fontWeight: 600,
      fontSize: 18,
      fontSmoothing: 'antialiased',
    },
    center: {
      textAlign: 'center',
    },
    icon: {
      position: 'absolute',
      top: 0,
      color: theme.color.basis4,
      fontSize: 14,
      padding: [12, 12],
      [theme.mediaQuery.tabletLandscapeAndUp]: {
        top: 2,
      },
      '&.left': {
        left: 0,
      },
      '&.right': {
        right: 0,
      },
    },
  };
}


export default useSheet(applyStyles)(GroupEditableTitle);
